import { DIALOG_DATA } from '@angular/cdk/dialog';

import { Component, inject } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { SpinnerComponent } from '@verify/shared-components/components';
import {
  Tenant,
  TenantFeature,
  TenantLanguage,
  TenantToggle,
  UserRule,
} from '@verify/shared-components/models';
import { TenantService } from '../../../services/tenant.service';

@Component({
  selector: 'app-edit-tenant-dialog',
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDialogModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    MatTabsModule,
    SpinnerComponent,
  ],
  templateUrl: './edit-tenant-dialog.component.html',
  styleUrl: './edit-tenant-dialog.component.scss',
})
export class EditTenantDialogComponent {
  private dialogRef = inject(MatDialogRef<void>);
  private tenantService = inject(TenantService);
  private formBuilder = inject(FormBuilder);

  dialogData: { tenant: Tenant } = inject(DIALOG_DATA);

  readonly form = this.formBuilder.nonNullable.group({
    id: ['', Validators.required],
    name: ['', Validators.required],
    features: new FormControl<TenantFeature[]>([]),
    toggles: new FormControl<TenantToggle[]>([]),
    languages: new FormControl<TenantLanguage[]>([]),
    defaultLanguage: new FormControl<TenantLanguage>(TenantLanguage.english),
    admin: this.formBuilder.group({
      email: ['', [Validators.email]],
      firstName: [''],
      lastName: [''],
      language: new FormControl<TenantLanguage>(TenantLanguage.english),
      rules: new FormControl<UserRule[]>([]),
    }),
    fseekConfig: this.formBuilder.group({
      tenantName: ['', Validators.required],
      quota: [null],
    }),
    fprintConfig: this.formBuilder.group({
      apiKey: [''],
    }),
    lythoConfig: this.formBuilder.group({
      apiKey: [''],
      lythoUrl: [''],
    }),
    bynderConfig: this.formBuilder.group({
      bynderUrl: [''],
      clientId: [''],
      clientSecret: [''],
      waitingRoom: [false],
    }),
    mediafilerConfig: this.formBuilder.group({
      mediafilerUrl: [''],
      apiKey: [''],
      apiUser: [''],
    }),
  });

  constructor() {
    if (this.dialogData?.tenant) {
      this.form.patchValue(this.dialogData.tenant);
    }
  }

  loading = false;

  get languages(): TenantLanguage[] {
    return Object.values(TenantLanguage) as TenantLanguage[];
  }

  get features(): TenantFeature[] {
    return Object.values(TenantFeature) as TenantFeature[];
  }

  get toggles(): TenantToggle[] {
    return Object.values(TenantToggle) as TenantToggle[];
  }

  get rules(): UserRule[] {
    return Object.values(UserRule) as UserRule[];
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    const {
      id,
      name,
      features,
      toggles,
      languages,
      defaultLanguage,
      admin,
      fseekConfig,
      fprintConfig,
      lythoConfig,
      bynderConfig,
      mediafilerConfig,
    } = this.form.getRawValue();
    this.loading = true;
    const data = {
      id: this.dialogData?.tenant?.id || id,
      name,
      features,
      toggles,
      languages,
      defaultLanguage,
      ...(fseekConfig.tenantName ? { fseekConfig } : {}),
      ...(fprintConfig.apiKey ? { fprintConfig } : {}),
      ...(lythoConfig.lythoUrl ? { lythoConfig } : {}),
      ...(bynderConfig.bynderUrl ? { bynderConfig } : {}),
      ...(mediafilerConfig.mediafilerUrl ? { mediafilerConfig } : {}),
    };
    if (this.dialogData?.tenant) {
      this.tenantService.updateTenant(data).subscribe(() => {
        this.dialogRef.close();
      });
    } else {
      this.tenantService.createTenant({ ...data, admin }).subscribe(() => {
        this.dialogRef.close();
      });
    }
  }
}
